import React from 'react';
import { useFormContext } from '../context/FormContext';
import FormField from './FormField';

/**
 * SmartFormField - Context-aware form field
 * @param {Object} props
 * @param {string} props.name - Field name (must match schema key)
 * @param {string} props.label - Field label
 * @param {string} props.type - Input type
 * @param {Object} props.inputProps - Additional props for the input
 */
export const SmartFormField = ({
    name,
    label,
    type = 'text',
    required,
    inputProps = {},
    className = '',
    children,
    ...props
}) => {
    const { formData, errors, handleFieldChange, handleFieldBlur, registerLabel, unregisterLabel } = useFormContext();

    React.useEffect(() => {
        if (label) {
            registerLabel(name, label);
        }
        return () => {
            unregisterLabel(name);
        };
    }, [name, label, registerLabel, unregisterLabel]);

    const value = formData[name] || '';
    const error = errors[name];

    const handleChange = (e) => {
        const newValue = e.target ? e.target.value : e;
        handleFieldChange(name, newValue);
    };

    const handleBlur = () => {
        handleFieldBlur(name);
    };

    // Merge default input props with context handlers
    const mergedInputProps = {
        id: name,
        name,
        value,
        onChange: handleChange,
        onBlur: handleBlur,
        type,
        placeholder: inputProps.placeholder,
        ...inputProps
    };

    return (
        <FormField
            label={label}
            htmlFor={name}
            error={error}
            required={required}
            type={type}
            className={className}
            inputProps={mergedInputProps}
            {...props}
        >
            {children}
        </FormField>
    );
};

export default SmartFormField;
